/**
 * UserSwitcherBlock
 *
 * Bottom-left user block shared between Klviar Studio (admin-console) and Klviar
 * Chat (agent-ui). Layout (expanded):
 *
 *   [ avatar + name (drop-up trigger) ]  [ switch-app icon button ]
 *
 * The drop-up trigger opens a menu with:
 *   - Account Settings (Account Region UI, new tab).
 *   - Logout.
 *
 * The switch-app button is a direct action (no menu) — single click jumps to
 * the other interface:
 *   - In Studio → /chat/.
 *   - In Chat → last visited Studio path (see useLastStudioPath).
 *
 * Collapsed mode renders the switch button above the avatar trigger.
 *
 * Single source of truth — never duplicate this block in admin-console or
 * agent-ui (see .github/copilot-instructions.md "Frontend Shared Components").
 */

import { useEffect, useRef, useState } from 'react';
import type { ComponentType } from 'react';
import {
  ChevronUp,
  LogOut,
  MessageSquare,
  LayoutDashboard,
  Settings as SettingsIcon,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { getStudioReturnUrl } from '../../hooks/useLastStudioPath';

export type UserSwitcherVariant = 'studio' | 'chat';

export interface UserSwitcherUser {
  name?: string | null;
  email?: string | null;
}

export interface UserSwitcherBlockProps {
  /** Current user (from useAuth). */
  user: UserSwitcherUser;
  /** Which app is rendering this block. Determines the "switch" target. */
  variant: UserSwitcherVariant;
  /** Account Region base URL (e.g. `https://auth-eu.atlas.local`). */
  identityPublicUrl: string;
  /** Logout handler from the host app. */
  onLogout: () => void | Promise<void>;
  /** Render avatar-only mode (when host sidebar is collapsed). */
  collapsed?: boolean;
  /** Localized labels (host injects translations). */
  labels: {
    /** "Switch to Klviar Studio" or similar (used in Chat variant). */
    switchStudio: string;
    /** "Open Klviar Chat" or similar (used in Studio variant). */
    openChat: string;
    /** "Account Settings". */
    accountSettings: string;
    /** "Log out". */
    logout: string;
    /** Fallback when user.name is missing. */
    user: string;
    /** aria-label for the user trigger button. */
    userMenu: string;
  };
  /** Override default cross-app target URL when needed. */
  switchHrefOverride?: string;
  /** Override the switch button label when needed. */
  switchLabelOverride?: string;
  /** Override the switch button icon when needed. */
  switchIconOverride?: ComponentType<{ className?: string }>;
  /** Optional extra menu link rendered above Account Settings. */
  extraMenuLink?: {
    label: string;
    href: string;
    icon: ComponentType<{ className?: string }>;
    newTab?: boolean;
  };
  /** Show or hide Account Settings entry in menu. Defaults to true. */
  showAccountSettings?: boolean;
  className?: string;
}

function getInitials(user: UserSwitcherUser): string {
  if (user.name && typeof user.name === 'string' && user.name.trim().length > 0) {
    return user.name
      .trim()
      .split(/\s+/)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  if (user.email && user.email.length > 0) {
    return user.email[0]!.toUpperCase();
  }
  return '?';
}

export function UserSwitcherBlock({
  user,
  variant,
  identityPublicUrl,
  onLogout,
  collapsed = false,
  labels,
  switchHrefOverride,
  switchLabelOverride,
  switchIconOverride,
  extraMenuLink,
  showAccountSettings = true,
  className,
}: UserSwitcherBlockProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside.
  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open]);

  const initials = getInitials(user);
  const displayName = user.name || user.email || labels.user;

  // Switch-app target.
  // - In Chat → go back to Studio (last known path or `/`).
  // - In Studio → open Chat at `/chat/`.
  const defaultSwitchHref = variant === 'chat' ? getStudioReturnUrl() : '/chat/';
  const switchHref = switchHrefOverride ?? defaultSwitchHref;
  const defaultSwitchLabel = variant === 'chat' ? labels.switchStudio : labels.openChat;
  const switchLabel = switchLabelOverride ?? defaultSwitchLabel;
  const defaultSwitchIcon = variant === 'chat' ? LayoutDashboard : MessageSquare;
  const SwitchIcon = switchIconOverride ?? defaultSwitchIcon;

  // Account Settings → AR UI, new tab.
  const accountSettingsHref = `${identityPublicUrl.replace(/\/$/, '')}/settings`;

  const handleLogout = async () => {
    setOpen(false);
    try {
      await onLogout();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Logout failed:', err);
    }
  };

  // Switch-app button (icon only, direct action — no menu).
  const switchButton = (
    <a
      href={switchHref}
      title={switchLabel}
      aria-label={switchLabel}
      className={cn(
        'flex items-center justify-center rounded-lg shrink-0',
        'w-9 h-9',
        'text-gray-600 dark:text-gray-300',
        'hover:bg-gray-100 dark:hover:bg-gray-800',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
      )}
    >
      <SwitchIcon className="h-5 w-5" />
    </a>
  );

  // User trigger (avatar + name + chevron) — opens the drop-up menu.
  const userTrigger = (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      className={cn(
        'flex items-center gap-3 p-2 rounded-lg transition-colors',
        'hover:bg-gray-100 dark:hover:bg-gray-800',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        open && 'bg-gray-100 dark:bg-gray-800',
        collapsed ? 'justify-center w-auto' : 'flex-1 min-w-0'
      )}
      aria-expanded={open}
      aria-haspopup="menu"
      aria-label={labels.userMenu}
      title={collapsed ? displayName : undefined}
    >
      <div
        className={cn(
          'w-9 h-9 rounded-full bg-blue-600 text-white',
          'flex items-center justify-center text-sm font-medium shrink-0'
        )}
      >
        {initials}
      </div>
      {!collapsed && (
        <>
          <div className="flex-1 min-w-0 text-left">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {displayName}
            </div>
            {user.email && user.name && (
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user.email}
              </div>
            )}
          </div>
          <ChevronUp
            className={cn(
              'h-4 w-4 text-gray-400 transition-transform',
              !open && 'rotate-180'
            )}
          />
        </>
      )}
    </button>
  );

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative',
        collapsed ? 'flex flex-col items-center' : 'flex items-center gap-2',
        className
      )}
    >
      {/* Expanded: switch button is a direct action next to the trigger.
          Collapsed: switch button lives inside the drop-up menu. */}
      {userTrigger}
      {!collapsed && switchButton}

      {open && (
        <div
          role="menu"
          aria-orientation="vertical"
          className={cn(
            'absolute z-50 w-60 py-1 rounded-lg shadow-lg',
            'bg-white dark:bg-gray-800',
            'border border-gray-200 dark:border-gray-700',
            'animate-in fade-in-0 zoom-in-95',
            // Drop-UP anchored to the trigger.
            'bottom-full mb-2',
            collapsed ? 'left-full ml-2' : 'left-0'
          )}
        >
          {/* User header (only in collapsed mode where the trigger hides it). */}
          {collapsed && (
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {displayName}
              </p>
              {user.email && (
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user.email}
                </p>
              )}
            </div>
          )}

          {/* Switch app (only in collapsed mode — in expanded mode it's a direct icon button). */}
          {collapsed && (
            <a
              href={switchHref}
              className={cn(
                'w-full flex items-center gap-3 px-4 py-2 text-sm',
                'text-gray-700 dark:text-gray-300',
                'hover:bg-gray-100 dark:hover:bg-gray-700',
                'focus-visible:outline-none focus-visible:bg-gray-100 dark:focus-visible:bg-gray-700'
              )}
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              <SwitchIcon className="h-4 w-4" />
              {switchLabel}
            </a>
          )}

          {extraMenuLink && (
            <a
              href={extraMenuLink.href}
              target={extraMenuLink.newTab ? '_blank' : undefined}
              rel={extraMenuLink.newTab ? 'noopener noreferrer' : undefined}
              className={cn(
                'w-full flex items-center gap-3 px-4 py-2 text-sm',
                'text-gray-700 dark:text-gray-300',
                'hover:bg-gray-100 dark:hover:bg-gray-700',
                'focus-visible:outline-none focus-visible:bg-gray-100 dark:focus-visible:bg-gray-700'
              )}
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              <extraMenuLink.icon className="h-4 w-4" />
              {extraMenuLink.label}
            </a>
          )}

          {showAccountSettings && (
            <a
              href={accountSettingsHref}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'w-full flex items-center gap-3 px-4 py-2 text-sm',
                'text-gray-700 dark:text-gray-300',
                'hover:bg-gray-100 dark:hover:bg-gray-700',
                'focus-visible:outline-none focus-visible:bg-gray-100 dark:focus-visible:bg-gray-700'
              )}
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              <SettingsIcon className="h-4 w-4" />
              {labels.accountSettings}
            </a>
          )}

          {(extraMenuLink || showAccountSettings) && (
            <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
          )}

          {/* Logout */}
          <button
            type="button"
            onClick={handleLogout}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-2 text-sm',
              'text-red-600 dark:text-red-400',
              'hover:bg-red-50 dark:hover:bg-red-900/20',
              'focus-visible:outline-none focus-visible:bg-red-50 dark:focus-visible:bg-red-900/20'
            )}
            role="menuitem"
          >
            <LogOut className="h-4 w-4" />
            {labels.logout}
          </button>
        </div>
      )}
    </div>
  );
}
